import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Get a single query parameter from the URL (case-insensitive lookup).
 *
 * **When to use this vs `useSearchParams`:**
 * - Use `useQueryParam` when URL params may arrive in different cases
 *   (e.g., `?USERID=123` vs `?userId=123` from external systems)
 * - Use React Router's `useSearchParams` for standard case-sensitive lookups
 *
 * @param {string} param - Parameter name to retrieve (case-insensitive match)
 * @returns {string | null} Parameter value, or null if not found
 *
 * @example
 * // URL: ?USERID=123 (external system sends uppercase)
 * const userId = useQueryParam('userId'); // "123" - matches despite case difference
 *
 * @example
 * // For case-sensitive lookups, prefer useSearchParams:
 * // const [searchParams] = useSearchParams();
 * // const userId = searchParams.get('userId');
 */
export function useQueryParam(param) {
  const { search } = useLocation();

  return React.useMemo(() => {
    if (!param) return null;
    const searchParams = new URLSearchParams(search);

    for (const [key, value] of searchParams) {
      if (key.toLowerCase() === param.toLowerCase()) {
        return value;
      }
    }
    return null;
  }, [search, param]);
}

const DELIMITER = '\0'; // null character to avoid conflicts

/**
 * Get multiple query parameters from the URL at once (case-insensitive lookup).
 *
 * **When to use this vs `useSearchParams`:**
 * - Use `useQueryParams` when URL params may arrive in different cases
 *   (e.g., `?DISABLEAUTH=true` vs `?DisableAuth=true` from different sources)
 * - Use React Router's `useSearchParams` for standard case-sensitive lookups
 *
 * **Current usage:** Feature flags provider uses this to handle flags that may
 * be set via URL with inconsistent casing.
 *
 * @template {string} T
 * @param {...T} queryParams - Parameter names to retrieve (case-insensitive match)
 * @returns {Record<T, string | null>} Object with param names as keys, values or null
 *
 * @example
 * // URL: ?userId=123&TOKEN=abc (external system sends mixed case)
 * const { userId, token } = useQueryParams('userId', 'token');
 * // { userId: "123", token: "abc" } - matches despite case differences
 *
 * @example
 * // For case-sensitive lookups, prefer useSearchParams:
 * // const [searchParams] = useSearchParams();
 * // const userId = searchParams.get('userId');
 * // const token = searchParams.get('token');
 */
export function useQueryParams(...queryParams) {
  const { search } = useLocation();

  // Create stable string for memoization - only changes when param names change
  const paramsKey = queryParams.join(DELIMITER);

  return React.useMemo(() => {
    const params = paramsKey.split(DELIMITER);
    const searchParams = new URLSearchParams(search);

    /** @type {Record<string, string | null>} */
    const result = {};

    for (const param of params) {
      result[param] = null;

      for (const [key, value] of searchParams) {
        if (key.toLowerCase() === param.toLowerCase()) {
          result[param] = value;
          break;
        }
      }
    }

    return result;
  }, [search, paramsKey]);
}
